// import

import * as Sentry from '@sentry/nextjs';
import {isClient, isProd} from '@sitearcade/is-env';
import LR from 'logrocket';
import connectReact from 'logrocket-react';

// types

type User = {
  id: string;
  name?: string;
  email?: string;
};

type UrlFn = (url?: string) => void;
export type TrackingOpts = Parameters<typeof LR.init>[1];

// vars

const key = process.env.LOGROCKET_KEY;
const withLogRocket = !!(key && isClient && isProd);

// fns

const noop = () => undefined;
const logRocketUserIds = new Set();

// config

// eslint-disable-next-line no-undef-init
let logSession: string | undefined = undefined;

if (withLogRocket) {
  LR.getSessionURL((url) => (logSession = url));
}

// export

export const initTracking = withLogRocket ?
  (opts: TrackingOpts = {}) => {
    LR.init(key as string, {
      rootHostname: 'sitearcade.com',
      shouldCaptureIP: true,
      shouldDebugLog: true,
      mergeIframes: true,

      release: [
        process.env.VERCEL_GIT_COMMIT_REF || 'HEAD',
        process.env.VERCEL_GIT_COMMIT_SHA.slice(0, 8) || 'X'.repeat(8),
      ].join('-'),

      ...opts,

      console: {
        isEnabled: true,
        shouldAggregateConsoleErrors: true,
        ...opts.console,
      },

      network: {
        isEnabled: true,
        requestSanitizer: undefined,
        responseSanitizer: undefined,
        ...opts.network,
      },

      dom: {
        isEnabled: true,
        inputSanitizer: false,
        textSanitizer: false,
        baseHref: undefined,
        ...opts.dom,
      },
    });

    connectReact(LR);

    LR.getSessionURL((logRocketUrl) => {
      if (!globalThis.dataLayer) {
        globalThis.dataLayer = [];
      }

      globalThis.dataLayer.push({
        event: 'logRocket', logRocketUrl,
      });
    });
  } : noop;

const _trackUser = (user?: User) =>
  Sentry.setUser({...user, ip_address: '{{auto}}'});

export const trackUser = withLogRocket ?
  (user?: User) => {
    if (user && !logRocketUserIds.has(user.id)) {
      logRocketUserIds.add(user.id);
      LR.identify(user.id, user);
    }

    _trackUser(user);
  } : _trackUser;

export const trackHeaders = withLogRocket ?
  () => ({'X-LogRocket-URL': logSession}) :
  () => ({});

export const getLogRocketSession = withLogRocket ?
  (fn?: UrlFn) => (fn ? LR.getSessionURL(fn) : logSession) :
  (fn?: UrlFn) => (fn ? fn(undefined) : undefined);
